import { Button, Monogram } from '@platform/ui';
import { ImageUp, Trash2 } from 'lucide-react';
import { useId, useRef, useState } from 'react';
import { toast } from 'sonner';

import { errorMessage } from '../lib/api';
import { IMAGE_ACCEPT, prepareImage } from '../lib/image-upload';

/**
 * Vista previa + "Subir imagen" / "Quitar" para una foto de perfil o el logo de una empresa. La
 * imagen se reduce en el navegador antes de subirla; la API valida los bytes igual.
 */
export function ImagePicker({
  name,
  colorKey,
  src,
  kind,
  hasImage,
  disabled,
  hint,
  onUpload,
  onRemove,
}: {
  name: string;
  colorKey?: string;
  src: string | null | undefined;
  kind: 'logo' | 'photo';
  hasImage: boolean;
  disabled?: boolean;
  hint?: string;
  onUpload: (dataUrl: string) => Promise<unknown>;
  onRemove: () => Promise<unknown>;
}) {
  const inputId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const run = (fn: () => Promise<unknown>) => {
    setBusy(true);
    fn()
      .catch((err: unknown) => {
        toast.error(errorMessage(err));
      })
      .finally(() => {
        setBusy(false);
        if (input.current) input.current.value = '';
      });
  };
  return (
    <div className="flex items-center gap-4">
      <Monogram
        name={name}
        {...(colorKey ? { colorKey } : {})}
        src={src}
        kind={kind}
        className={kind === 'photo' ? 'size-16 rounded-full text-lg' : 'size-16 rounded-xl text-lg'}
      />
      <div className="flex min-w-0 flex-col gap-2">
        <div className="flex flex-wrap gap-2">
          <input
            ref={input}
            id={inputId}
            type="file"
            tabIndex={-1}
            aria-hidden
            accept={IMAGE_ACCEPT}
            className="sr-only"
            disabled={disabled === true || busy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              run(async () => {
                await onUpload(await prepareImage(file, { kind }));
              });
            }}
          />
          <Button
            size="sm"
            variant="outline"
            disabled={disabled === true || busy}
            onClick={() => {
              input.current?.click();
            }}
          >
            <ImageUp /> {busy ? 'Subiendo…' : hasImage ? 'Cambiar imagen' : 'Subir imagen'}
          </Button>
          {hasImage ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={disabled === true || busy}
              onClick={() => {
                run(onRemove);
              }}
            >
              <Trash2 /> Quitar
            </Button>
          ) : null}
        </div>
        {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      </div>
    </div>
  );
}
